import React from "react";
import { NumberInput, Divider } from "@heroui/react";
import { t } from "@lingui/core/macro";

interface DurationInputProps {
  value?: number;
  onValueChange?: (totalMinutes: number) => void;
  label?: React.ReactNode;
  isDisabled?: boolean;
  isRequired?: boolean;
  className?: string;
  minuteStep?: number;
  showDays?: boolean;
}

interface DurationValues {
  days: number;
  hours: number;
  minutes: number;
}

export const DurationInput: React.FC<DurationInputProps> = ({
  value = 0,
  onValueChange,
  label,
  isDisabled = false,
  isRequired = false,
  className = "",
  minuteStep = 5,
  showDays = true,
}) => {
  // Convert total minutes to days, hours, minutes
  const calculateDurationValues = (totalMinutes: number): DurationValues => {
    // Without a days field, whole days fold into hours so none are hidden.
    const days = showDays ? Math.floor(totalMinutes / (24 * 60)) : 0;
    const remainingMinutes = totalMinutes - days * 24 * 60;
    const hours = Math.floor(remainingMinutes / 60);
    const minutes = remainingMinutes % 60;

    return { days, hours, minutes };
  };

  // Calculate total minutes from days, hours, minutes
  const calculateTotalMinutes = (values: DurationValues): number => {
    return values.days * 24 * 60 + values.hours * 60 + values.minutes;
  };

  // Fully controlled: derive the day/hour/minute breakdown straight from the
  // `value` prop instead of mirroring it into state and re-syncing via an
  // effect. Every call site passes `value` + `onValueChange`, so edits round-trip
  // through the parent.
  const durationValues = calculateDurationValues(value);

  // Handle individual field changes
  const handleFieldChange = (field: keyof DurationValues, newValue: number) => {
    // A cleared field reports NaN, which would poison the total.
    const validValue = Number.isNaN(newValue) ? 0 : Math.max(0, newValue);

    const updatedValues = {
      ...durationValues,
      [field]: validValue,
    };

    // Notify parent component about the change
    onValueChange?.(calculateTotalMinutes(updatedValues));
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <div>
          <label className="text-default-500 text-xs">
            {label}
            {isRequired && <span className="text-danger ml-1">*</span>}
          </label>
        </div>
      )}
      <div className="border-default-200 rounded-xl border-1 p-1">
        <div className="flex flex-col gap-0 sm:flex-row">
          {showDays && (
            <>
              <div className="flex-1">
                <NumberInput
                  label={t`Dni`}
                  value={durationValues.days}
                  onValueChange={(value) => handleFieldChange("days", value)}
                  minValue={0}
                  step={1}
                  isDisabled={isDisabled}
                  className="w-full"
                  size="sm"
                  classNames={{
                    inputWrapper: "rounded-tr-none rounded-br-none",
                    label: "text-xs",
                  }}
                />
              </div>

              <Divider orientation="vertical" className="hidden sm:block" />
              <Divider className="sm:hidden" />
            </>
          )}

          <div className="flex-1">
            <NumberInput
              label={t`Ur`}
              value={durationValues.hours}
              onValueChange={(value) => handleFieldChange("hours", value)}
              minValue={0}
              maxValue={showDays ? 23 : undefined}
              step={1}
              isDisabled={isDisabled}
              className="w-full"
              size="sm"
              classNames={{
                inputWrapper: showDays
                  ? "rounded-none"
                  : "rounded-tr-none rounded-br-none",
                label: "text-xs",
              }}
            />
          </div>

          <Divider orientation="vertical" className="hidden sm:block" />
          <Divider className="sm:hidden" />

          <div className="flex-1">
            <NumberInput
              label={t`Minut`}
              value={durationValues.minutes}
              onValueChange={(value) => handleFieldChange("minutes", value)}
              minValue={0}
              maxValue={59}
              step={minuteStep}
              isDisabled={isDisabled}
              className="w-full"
              size="sm"
              classNames={{
                inputWrapper: "rounded-tl-none rounded-bl-none",
                label: "text-xs",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
